import React from "react";
import styled from "styled-components";
import Colors from "../../ui/global/Colors";
import Fonts from "../../ui/global/Fonts";

interface PriceCardMiniProps {
  $colorMode: 'light' | 'dark';
  $selected: boolean;
}

const PriceCardMini = styled.div<PriceCardMiniProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 280px;
  background-color: ${props => {
    return props.$colorMode === 'light' 
      ? Colors.white 
      : Colors.black + 30;
  }};
  border-radius: 12px;
  border: ${props => {
    if (props.$selected) {
      return props.$colorMode === 'light'
        ? `1px solid ${Colors.grey8}`
        : `1px solid ${Colors.grey2}`;
    }
    return props.$colorMode === 'light' 
      ? `1px solid ${Colors.grey2}` 
      : `1px solid ${Colors.grey7}`;
  }};
  padding: 8px;
  gap: 12px;
  cursor: pointer;
  transform: ${props => props.$selected ? 'scale(1.08)' : 'scale(1)'};
  transform-origin: center;
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: ${props => props.$selected ? 'scale(1.11)' : 'scale(1.03)'};
  }

  &:active {
    transform: ${props => props.$selected ? 'scale(1.06)' : 'scale(0.98)'};
  }
`;

export const BestValueRibbon = styled.div<{ $colorMode: 'light' | 'dark' }>`
  position: absolute;
  top: 14px;
  right: -32px;
  background-color: ${props => props.$colorMode === 'light' ? Colors.grey8 : Colors.grey2};
  color: ${props => props.$colorMode === 'light' ? Colors.white : Colors.black};
  font-family: ${Fonts.lexendBold.fontFamily};
  font-size: 10px;
  width: 120px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(45deg);
  z-index: 10;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  pointer-events: none;
`;

interface TopContainerProps {
  $colorMode: 'light' | 'dark';
  $selected: boolean;
}

const TopContainer = styled.div<TopContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  background-color: ${props => props.$colorMode === 'light' 
    ? Colors.grey1+90 
    : Colors.black+90
  };
  border-bottom: 1px solid ${props => props.$colorMode === 'light' ? Colors.grayPale : Colors.grey8+90};
  padding: 12px 20px;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const CreditsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const PlanName = styled.h2<{ $visible: boolean; $colorMode?: 'light' | 'dark'; $selected: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: baseline;
  width: fit-content;
  height: 31px;
  border-radius: 48px;
  border: 1px solid ${props => props.$colorMode === 'light' ? Colors.grey2 : Colors.grey7};
  background-color: ${props => props.$colorMode === 'light' ? Colors.white+80 : Colors.grey9};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 14px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey8 : Colors.grey1};
  padding: 4px 16px;
  margin: 0 auto;
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
`;

const CreditsNumber = styled.h1<{ $colorMode?: 'light' | 'dark'; $selected: boolean }>`
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 52px;
  line-height: 1.2em;
  color: ${props => props.$colorMode === 'light' ? Colors.black : Colors.white};
  margin: 0 4px;

  @media (max-width: 768px) {
    font-size: 44px;
  }
`;

const CreditsLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const CreditsLabel = styled.span<{ $colorMode?: 'light' | 'dark'; $selected: boolean }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 18px;
  line-height: 1.2em;
  color: ${props => props.$colorMode === 'light' ? Colors.grey8 : Colors.grey3};

  @media (max-width: 768px) {
    line-height: 1.1em;
    font-size: 16px;
  }
`;

const PriceContainer = styled.div<{ $colorMode?: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 16px 8px 16px;
  gap: 8px;
`;

const PriceItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  line-height: 1.3em;
  width: 100%;
`;

const ItemText = styled.p<{ $colorMode?: 'light' | 'dark'; $selected: boolean }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 14px;
  color: ${props => {
    return props.$colorMode === 'light' ? Colors.grey6 : Colors.grey4;
  }};
  margin: 0;
  text-align: center;
  white-space: pre-wrap;
`;

interface PriceOptionsMiniProps {
  planName: string;
  credits: string;
  priceText: string;
  colorMode?: 'light' | 'dark';
  selected?: boolean;
  onSelect?: () => void;
  bestValue?: boolean;
}

const PriceOptionsMini: React.FC<PriceOptionsMiniProps> = ({
  planName,
  credits,
  priceText,
  colorMode = 'dark',
  selected = false,
  onSelect,
  bestValue = false,
}) => {
  const handleClick = () => {
    if (onSelect) {
      onSelect();
    }
  };
  return (
    <PriceCardMini 
      $colorMode={colorMode} 
      $selected={selected}
      onClick={handleClick}
    >
      {bestValue && <BestValueRibbon $colorMode={colorMode}>Best Value</BestValueRibbon>}
      <TopContainer $colorMode={colorMode} $selected={selected}>
        <PlanName $visible={!!planName} $colorMode={colorMode} $selected={selected}>{planName}</PlanName>
        <CreditsContainer>
          <CreditsNumber $colorMode={colorMode} $selected={selected}>{credits}</CreditsNumber>
          <CreditsLabelContainer>
            <CreditsLabel $colorMode={colorMode} $selected={selected}>AI credits</CreditsLabel>
            <CreditsLabel $colorMode={colorMode} $selected={selected}>/ month</CreditsLabel>
          </CreditsLabelContainer>
        </CreditsContainer>
      </TopContainer>
      
      <PriceContainer $colorMode={colorMode}>
        <PriceItem>
          <ItemText $colorMode={colorMode} $selected={selected}>{priceText}</ItemText>
        </PriceItem>
      </PriceContainer>
    </PriceCardMini>
  );
};

export default PriceOptionsMini;